import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company_name: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register({
      ...form,
      email: form.email.trim().toLowerCase(),
    });
    setLoading(false);
    if (res.ok) {
      toast.success("Workspace created");
      navigate("/app");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF7EF]">
      <div className="hidden lg:flex flex-col justify-between bg-neutral-950 text-white p-12 bg-grid-dark relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: "radial-gradient(600px 400px at 30% 20%, rgba(201,169,97,0.25), transparent 60%)"}}></div>
        <div className="relative">
          <Brand size="md" variant="dark" asLink />
        </div>
        <div className="relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#D4B87D] mb-4 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[#C9A961]"></span>
            New engagement
          </div>
          <h1 className="font-serif font-black text-5xl xl:text-6xl tracking-[-0.02em] leading-[0.95]">
            Open a private<br />
            <span className="italic text-gold-gradient">client workspace.</span>
          </h1>
          <p className="mt-6 text-neutral-400 max-w-md leading-relaxed">
            You'll be the HR admin of this workspace. Add employees, run payroll, and start issuing payslips within minutes — reviewed by our team.
          </p>
        </div>
        <div className="relative font-mono text-xs text-neutral-500 flex justify-between">
          <span>© 2026 HG Consultancy · Harguaon Group</span>
          <span className="text-[#D4B87D]">v 1.0</span>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-16 py-12">
        <div className="max-w-md w-full mx-auto">
          <div className="lg:hidden mb-8"><Brand size="md" variant="light" asLink /></div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-3">Create</div>
          <h2 className="font-serif font-black text-4xl tracking-[-0.02em] mb-2">Register your firm.</h2>
          <p className="text-sm text-neutral-600 mb-10">One workspace per client company. Add more later.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Company / Client name</label>
                <input required value={form.company_name} onChange={set("company_name")} data-testid="register-company"
                  className="w-full px-3 py-3 bg-white border border-[#E8DFC5] focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961] outline-none transition-colors font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Your name</label>
                <input required value={form.name} onChange={set("name")} data-testid="register-name"
                  className="w-full px-3 py-3 bg-white border border-[#E8DFC5] focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961] outline-none transition-colors font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Work email</label>
                <input required type="email" value={form.email} onChange={set("email")} data-testid="register-email"
                  className="w-full px-3 py-3 bg-white border border-[#E8DFC5] focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961] outline-none transition-colors font-mono text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Password (min 6)</label>
                <input required type="password" minLength={6} value={form.password} onChange={set("password")} data-testid="register-password"
                  className="w-full px-3 py-3 bg-white border border-[#E8DFC5] focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961] outline-none transition-colors font-mono text-sm" />
              </div>
            </div>

            {error && (
              <div data-testid="register-error" className="border border-[#B33A1F] bg-[#B33A1F]/5 px-3 py-2 text-sm text-[#B33A1F] font-mono">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full flex items-center justify-between px-4 py-3.5 bg-neutral-950 text-[#D4B87D] font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors disabled:opacity-60"
            >
              {loading ? "Creating workspace…" : "Create workspace"}
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#E8DFC5] text-sm text-neutral-600">
            Already have a workspace?{" "}
            <Link to="/login" className="text-[#8C6F1E] hover:underline" data-testid="link-to-login">
              Sign in →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
